import { useEffect, useState } from 'react';

/**
 * useFloatingCollision — keeps a fixed/sticky floating control from ever
 * covering interactive page content. Returns `true` when the control's
 * rectangle (inflated by `margin`) intersects any VISIBLE interactive element,
 * so the caller can hide/disable the control at those scroll positions.
 *
 * This makes the hard rule literally true at every scroll position ("no
 * floating control may cover any portion of a visible button") instead of
 * relying on a single static offset. Throttled with rAF + passive scroll.
 */
const TARGET_SELECTOR =
  'button,a[href],input,select,textarea,[role="button"],[role="tab"],[data-mobile-action]';

export function useFloatingCollision(ref, { margin = 16, enabled = true } = {}) {
  const [colliding, setColliding] = useState(false);

  useEffect(() => {
    if (!enabled) { setColliding(false); return undefined; }
    let raf = 0;

    const visible = (el) => {
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      if (parseFloat(cs.opacity) === 0 || cs.pointerEvents === 'none') return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };

    const check = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) { setColliding(false); return; }
      const box = { left: r.left - margin, right: r.right + margin, top: r.top - margin, bottom: r.bottom + margin };
      const vh = window.innerHeight;
      let hit = false;
      const targets = document.querySelectorAll(TARGET_SELECTOR);
      for (let i = 0; i < targets.length; i += 1) {
        const t = targets[i];
        if (el.contains(t) || t.contains(el)) continue;
        if (t.closest('[data-floating-control="true"]')) continue;  // ignore other FABs
        const tr = t.getBoundingClientRect();
        if (tr.width === 0 || tr.height === 0) continue;
        if (tr.bottom < 0 || tr.top > vh) continue;                 // off-screen
        if (box.right <= tr.left || box.left >= tr.right || box.bottom <= tr.top || box.top >= tr.bottom) continue;
        if (!visible(t)) continue;
        hit = true;
        break;
      }
      setColliding(hit);
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(check); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const iv = setInterval(schedule, 500);  // catch async content / layout shifts
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, margin, enabled]);

  return colliding;
}
